import { useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useHasPermission } from '@/auth/auth-store';
import { useT } from '@/i18n/use-t';
import {
  useApplyProvisionCompositionDraft,
  useDesiredStatePreview,
  usePreviewProvisionCompositionDraft,
  useProvisionCompositionCapabilities,
  useProvisionCompositionCandidates,
  useProvisionCompositionDraft,
  useProvisionCompositionProfile,
  useProvisionEndpointInventory,
  useProvisionEquipmentCatalogue,
  useProvisionSoftwareCatalogue,
  useSaveProvisionCompositionDraft,
  type ProvisionApplication,
  type ProvisionCompositionCandidateRequest,
  type ProvisionCompositionCandidateResponse,
  type ProvisionCompositionDraftPreview,
  type DriftItem,
  type ProvisionEndpoint,
  type ProvisionEquipmentCatalogueModel,
  type ProvisionSoftwareCatalogueComponent,
  type ProvisionAccessMode,
  type ProvisionEndpointInventory,
} from '@/hooks/use-provision';
import {
  buildManagedCompositionDraft,
  compatibilitySelection,
  componentVersion,
  connectionProfile,
  createManagedCompositionEditor,
  equipmentRevision,
  managedCompositionEditorReducer,
  type CompositionTranslate,
  type ManagedCompositionEditorAction,
  type ManagedCompositionEditorState,
  type ManagedHardwareRoleState,
  type ManagedSoftwareState,
} from './composition-editor-state';
import { AdvancedCompositionEditor, CompositionHistory } from './advanced-composition';
import { CompositionDriftPanel } from './composition-drift-panel';
import { certificationLabel } from './certification-label';

type ApplicationType = ProvisionApplication['intrinsicType'];
type CandidateKind = ProvisionCompositionCandidateRequest['kind'];
type AuthoringPath = 'managed' | 'manual';

/**
 * Keep the pure draft builder reactive to catalogue delivery and locale changes.
 * `useT` changes identity only when its active/fallback dictionaries change, so
 * including the translator here updates validation text without making an
 * otherwise unchanged render rebuild the draft.
 */
export function useManagedCompositionDraftBuild(params: {
  editor: ManagedCompositionEditorState | null;
  equipmentCatalogue: ProvisionEquipmentCatalogueModel[];
  softwareCatalogue: ProvisionSoftwareCatalogueComponent[];
  translate: CompositionTranslate;
}) {
  return useMemo(
    () =>
      params.editor === null
        ? { draft: null, errors: [] }
        : buildManagedCompositionDraft({
            translate: params.translate,
            state: params.editor,
            equipmentCatalogue: params.equipmentCatalogue,
            softwareCatalogue: params.softwareCatalogue,
          }),
    [params.editor, params.equipmentCatalogue, params.softwareCatalogue, params.translate],
  );
}

export function availableCompositionAuthoringPath(
  requested: AuthoringPath,
  managedWritesEnabled: boolean,
): AuthoringPath {
  return managedWritesEnabled ? requested : 'manual';
}

export function ManagedComposition({
  endpoints,
  drift,
  onOpenRollouts,
  accessMode = 'admin',
}: {
  endpoints: ProvisionEndpoint[];
  drift: DriftItem[];
  onOpenRollouts?: () => void;
  accessMode?: ProvisionAccessMode;
}) {
  const t = useT('admin_ui');
  const canAdmin = useHasPermission('provision.admin');
  const canConfigure = canAdmin || accessMode === 'technician';
  const capabilities = useProvisionCompositionCapabilities();
  const managedWritesEnabled = capabilities.data?.managedWritesEnabled ?? true;
  const [applicationType, setApplicationType] = useState<ApplicationType | ''>('');
  const [endpointId, setEndpointId] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [showIncompatible, setShowIncompatible] = useState(false);
  const [authoringPath, setAuthoringPath] = useState<AuthoringPath>('managed');
  const [editor, setEditor] = useState<ManagedCompositionEditorState | null>(null);
  const [previewResult, setPreviewResult] = useState<ProvisionCompositionDraftPreview | null>(null);
  const [applyResult, setApplyResult] = useState<{
    changed: boolean;
    compositionRevision: number;
    desiredStateRevision: number;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [confirmLargeScope, setConfirmLargeScope] = useState(false);
  const initializedKey = useRef('');
  const selectedEndpoint = endpoints.find((endpoint) => endpoint.id === endpointId) ?? null;
  const selectedApplication =
    selectedEndpoint?.applications.find((application) => application.id === applicationId) ?? null;
  const eligibleEndpoints =
    applicationType === ''
      ? []
      : endpoints.filter((endpoint) =>
          endpoint.applications.some(
            (application) => application.intrinsicType === applicationType,
          ),
        );
  const eligibleApplications =
    selectedEndpoint?.applications.filter(
      (application) => application.intrinsicType === applicationType,
    ) ?? [];
  const selectedApplicationId = selectedApplication?.id ?? null;
  const selectedEndpointId = selectedEndpoint?.id ?? null;
  const profile = useProvisionCompositionProfile(selectedApplicationId, accessMode);
  const draft = useProvisionCompositionDraft(selectedApplicationId, accessMode);
  const desired = useDesiredStatePreview(selectedApplicationId, accessMode);
  const equipment = useProvisionEquipmentCatalogue(selectedApplication?.intrinsicType ?? null, {
    mode: accessMode,
  });
  const software = useProvisionSoftwareCatalogue(selectedApplication?.intrinsicType ?? null, {
    mode: accessMode,
  });
  const inventory = useProvisionEndpointInventory(selectedEndpointId, accessMode);
  const saveDraft = useSaveProvisionCompositionDraft(accessMode);
  const previewDraft = usePreviewProvisionCompositionDraft(accessMode);
  const applyDraft = useApplyProvisionCompositionDraft(accessMode);

  useEffect(() => {
    setAuthoringPath((current) => availableCompositionAuthoringPath(current, managedWritesEnabled));
  }, [managedWritesEnabled]);

  useEffect(() => {
    if (
      selectedApplication === null ||
      !profile.isSuccess ||
      !draft.isSuccess ||
      !desired.isSuccess ||
      !software.isSuccess
    ) {
      return;
    }
    const key = compositionSourceKey(
      selectedApplication.id,
      draft.data?.version ?? null,
      profile.data.selected?.versionId ?? null,
      software.data,
    );
    if (initializedKey.current === key) return;
    initializedKey.current = key;
    setEditor(
      createManagedCompositionEditor({
        applicationId: selectedApplication.id,
        applicationType: selectedApplication.intrinsicType,
        profile: profile.data,
        draft: draft.data,
        desiredValues: desired.data.values,
        softwareCatalogue: software.data,
      }),
    );
    setPreviewResult(null);
    setApplyResult(null);
    setReason('');
    setConfirmLargeScope(false);
  }, [
    desired.data,
    desired.isSuccess,
    draft.data,
    draft.isSuccess,
    profile.data,
    profile.isSuccess,
    selectedApplication,
    software.data,
    software.isSuccess,
  ]);

  const dispatch: Dispatch<ManagedCompositionEditorAction> = (action) => {
    setEditor((current) =>
      current === null ? null : managedCompositionEditorReducer(current, action, t),
    );
    setPreviewResult(null);
    setApplyResult(null);
  };
  const selectedSoftwareVersionIds = useMemo(
    () =>
      editor === null
        ? []
        : Object.values(editor.software).flatMap((component) =>
            component.componentVersionId === null ? [] : [component.componentVersionId],
          ),
    [editor],
  );
  const built = useManagedCompositionDraftBuild({
    editor,
    equipmentCatalogue: equipment.data ?? [],
    softwareCatalogue: software.data ?? [],
    translate: t,
  });
  const warningFindings =
    previewResult?.validation.findings.filter((finding) => finding.acknowledgeable) ?? [];
  const warningsAcknowledged = warningFindings.every((finding) =>
    editor?.acknowledgedWarningCodes.includes(finding.reason.code),
  );
  const busy = saveDraft.isPending || previewDraft.isPending || applyDraft.isPending;

  async function saveAndPreview() {
    if (editor === null || selectedApplication === null || built.draft === null) return;
    setApplyResult(null);
    const saved = await saveDraft.mutateAsync({
      applicationId: selectedApplication.id,
      draft: built.draft,
    });
    initializedKey.current = compositionSourceKey(
      selectedApplication.id,
      saved.version,
      profile.data?.selected?.versionId ?? null,
      software.data ?? [],
    );
    setEditor((current) =>
      current === null
        ? null
        : {
            ...current,
            sourceDraftVersion: saved.version,
            sourceMode: 'managed',
            convertManualDraft: false,
            dirty: false,
          },
    );
    const preview = await previewDraft.mutateAsync({
      applicationId: selectedApplication.id,
      expectedDraftVersion: saved.version,
    });
    setPreviewResult(preview);
  }

  async function applyPreview() {
    if (previewResult === null || selectedApplication === null) return;
    const result = await applyDraft.mutateAsync({
      applicationId: selectedApplication.id,
      request: {
        schemaVersion: 1,
        expectedDraftVersion: previewResult.draftVersion,
        expectedCompositionRevision: previewResult.expectedCompositionRevision,
        expectedProfileRevision: previewResult.expectedProfileRevision,
        expectedHistorySequence: previewResult.expectedHistorySequence,
        expectedObservationGeneration: previewResult.expectedObservationGeneration,
        expectedRecords: previewResult.expectedRecords,
        previewChecksum: previewResult.previewChecksum,
        reason,
        confirmLargeScope,
      },
    });
    setApplyResult(result);
  }

  return (
    <div className="space-y-5">
      <section className="rounded-lg border bg-card p-4">
        <div className="mb-4">
          <h2 className="text-lg font-semibold">
            {t('provision.composition.title', 'Terminal composition')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {accessMode === 'technician'
              ? t(
                  'provision.composition.introTechnician',
                  'Choose an assigned terminal and its equipment. Provision resolves the compatible adapter and version automatically before you preview and apply.',
                )
              : t(
                  'provision.composition.introAdmin',
                  'Select an application, then work left-to-right through compatible catalogue choices. Selections remain local until you save a draft, and no applied composition changes until the final confirmation.',
                )}
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label={t('provision.composition.stepApplicationType', '1. Application type')}>
            <Select
              aria-label={t('provision.composition.applicationType', 'Application type')}
              value={applicationType}
              onChange={(event) => {
                setApplicationType(event.target.value as ApplicationType | '');
                setEndpointId('');
                setApplicationId('');
                setAuthoringPath(
                  availableCompositionAuthoringPath('managed', managedWritesEnabled),
                );
                initializedKey.current = '';
                setEditor(null);
              }}
            >
              <option value="">
                {t('provision.composition.selectApplicationType', 'Select POS, SCO, or Attendant')}
              </option>
              <option value="pos">POS</option>
              <option value="sco">SCO</option>
              <option value="attendant">{t('provision.composition.attendant', 'Attendant')}</option>
            </Select>
          </Field>
          <Field label={t('provision.composition.stepEndpoint', '2. Physical endpoint')}>
            <Select
              aria-label={t('provision.composition.physicalEndpoint', 'Physical endpoint')}
              disabled={applicationType === ''}
              value={endpointId}
              onChange={(event) => {
                setEndpointId(event.target.value);
                setApplicationId('');
                setAuthoringPath(
                  availableCompositionAuthoringPath('managed', managedWritesEnabled),
                );
                initializedKey.current = '';
                setEditor(null);
              }}
            >
              <option value="">
                {t('provision.composition.selectEndpoint', 'Select endpoint')}
              </option>
              {eligibleEndpoints.map((endpoint) => (
                <option key={endpoint.id} value={endpoint.id}>
                  {endpoint.code} · {endpoint.displayName}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label={t('provision.composition.stepApplicationInstance', '3. Application instance')}
          >
            <Select
              aria-label={t('provision.composition.applicationInstance', 'Application instance')}
              disabled={selectedEndpoint === null}
              value={applicationId}
              onChange={(event) => {
                setApplicationId(event.target.value);
                setAuthoringPath(
                  availableCompositionAuthoringPath('managed', managedWritesEnabled),
                );
                initializedKey.current = '';
                setEditor(null);
              }}
            >
              <option value="">
                {t('provision.composition.selectApplication', 'Select application')}
              </option>
              {eligibleApplications.map((application) => (
                <option key={application.id} value={application.id}>
                  {application.code}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </section>

      {selectedApplication !== null && (
        <>
          <section className="rounded-lg border p-4">
            {profile.isLoading ? (
              <p className="text-sm text-muted-foreground">
                {t('provision.composition.resolvingProfile', 'Resolving composition profile…')}
              </p>
            ) : profile.data?.selected === null || profile.data === undefined ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                {t(
                  'provision.composition.noProfile',
                  'No applicable published {type} profile. Managed authoring is unavailable until a profile is published.',
                ).replace('{type}', selectedApplication.intrinsicType.toUpperCase())}
              </div>
            ) : (
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold">{profile.data.selected.profileName}</h2>
                    <Badge variant="outline">v{profile.data.selected.version}</Badge>
                    <Badge variant="outline">
                      {profile.data.selectionMode.replaceAll('_', ' ')}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t(
                      'provision.composition.profileSummary',
                      '{roles} equipment roles · {capabilities} required software capabilities',
                    )
                      .replace('{roles}', String(profile.data.selected.roles.length))
                      .replace(
                        '{capabilities}',
                        String(profile.data.selected.requiredComponentCapabilities.length),
                      )}
                  </p>
                </div>
                {authoringPath === 'managed' && accessMode === 'admin' && (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={showIncompatible}
                      onChange={(event) => setShowIncompatible(event.target.checked)}
                    />
                    {t(
                      'provision.composition.showIncompatible',
                      'Show incompatible choices and reasons',
                    )}
                  </label>
                )}
              </div>
            )}
          </section>

          {canAdmin && (
            <section className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
              <div>
                <div className="font-medium">
                  {t('provision.composition.authoringPath', 'Authoring path')}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t(
                    'provision.composition.authoringPathHelp',
                    'Both paths save the same canonical Provision composition.',
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={authoringPath === 'managed' ? 'default' : 'outline'}
                  disabled={!managedWritesEnabled}
                  onClick={() => setAuthoringPath('managed')}
                >
                  {t('provision.composition.managedCatalogue', 'Managed catalogue')}
                </Button>
                <Button
                  size="sm"
                  variant={authoringPath === 'manual' ? 'default' : 'outline'}
                  onClick={() => setAuthoringPath('manual')}
                >
                  {t('provision.composition.advancedManual', 'Advanced/manual')}
                </Button>
              </div>
            </section>
          )}

          {!managedWritesEnabled && (
            <section className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
              <div className="font-medium">
                {t('provision.composition.writesPaused', 'Managed catalogue writes are paused')}
              </div>
              <p className="mt-1 text-muted-foreground">
                {t(
                  'provision.composition.writesPausedHelp',
                  'Catalogue data remains readable. Advanced/manual authoring and all Custom/legacy records remain available to Provision Admins during rollout or rollback.',
                )}
              </p>
            </section>
          )}

          {managedWritesEnabled &&
            authoringPath === 'managed' &&
            editor !== null &&
            profile.data?.selected !== null && (
              <>
                {editor.sourceMode === 'manual' && (
                  <section className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-sm">
                    <div className="flex items-center gap-2 font-medium">
                      {t(
                        'provision.composition.manualDraftNotice',
                        'Saved draft is Advanced/manual',
                      )}
                      <Badge variant="outline">
                        {t('provision.composition.customLegacy', 'Custom/legacy')}
                      </Badge>
                    </div>
                    <p className="mt-1 text-muted-foreground">
                      {t(
                        'provision.composition.manualDraftHelp',
                        'Its raw values are shown as Custom/legacy and will not be replaced by automatic selection. Converting requires an explicit confirmation after you choose a complete managed composition.',
                      )}
                    </p>
                    <label className="mt-3 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editor.convertManualDraft}
                        onChange={(event) =>
                          dispatch({
                            type: 'confirm-manual-conversion',
                            confirmed: event.target.checked,
                          })
                        }
                      />
                      {t(
                        'provision.composition.replaceManualDraft',
                        'Replace the saved manual draft with my reviewed managed selections',
                      )}
                    </label>
                  </section>
                )}

                {editor.lastInvalidation !== null && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
                    {editor.lastInvalidation}
                  </div>
                )}

                <section className="space-y-3">
                  <div>
                    <h2 className="text-lg font-semibold">
                      {t('provision.composition.equipmentRoles', 'Equipment roles')}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        'provision.composition.equipmentRolesHelp',
                        'Required, optional, and conditional roles come from the selected profile; this form does not hard-code POS/SCO/Attendant equipment lists.',
                      )}
                    </p>
                  </div>
                  {Object.values(editor.hardware).map((role) => (
                    <ManagedRoleCard
                      key={role.roleKey}
                      applicationId={selectedApplication.id}
                      role={role}
                      showIncompatible={showIncompatible}
                      equipmentCatalogue={equipment.data ?? []}
                      softwareCatalogue={software.data ?? []}
                      selectedSoftwareVersionIds={selectedSoftwareVersionIds}
                      inventoryPorts={inventory.data?.ports ?? []}
                      accessMode={accessMode}
                      dispatch={dispatch}
                    />
                  ))}
                </section>

                {accessMode === 'admin' && (
                  <SoftwareSection
                    applicationId={selectedApplication.id}
                    editor={editor}
                    showIncompatible={showIncompatible}
                    catalogue={software.data ?? []}
                    requiredCapabilities={
                      profile.data?.selected?.requiredComponentCapabilities ?? []
                    }
                    optionalCapabilities={
                      profile.data?.selected?.optionalComponentCapabilities ?? []
                    }
                    accessMode={accessMode}
                    dispatch={dispatch}
                  />
                )}

                {accessMode === 'technician' && (
                  <TechnicianDeliveryStatus
                    inventory={inventory.data}
                    applicationId={selectedApplication.id}
                  />
                )}

                <section className="rounded-lg border p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="font-semibold">
                        {t('provision.composition.validateAndConfirm', 'Validate and confirm')}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'provision.composition.validateAndConfirmHelp',
                          'Saving stores only an optimistic authoring draft. Preview and apply rerun the complete server compatibility engine.',
                        )}
                      </p>
                    </div>
                    <Button
                      disabled={!canConfigure || built.draft === null || busy}
                      onClick={() => void saveAndPreview()}
                    >
                      {busy
                        ? t('provision.composition.working', 'Working…')
                        : t('provision.composition.saveDraftPreview', 'Save draft & preview')}
                    </Button>
                  </div>
                  {!canConfigure && (
                    <p className="mt-3 text-sm text-muted-foreground">
                      {t(
                        'provision.composition.permissionRequired',
                        'Permission is required to save or apply a managed composition.',
                      )}
                    </p>
                  )}
                  {built.errors.length > 0 && (
                    <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-destructive">
                      {built.errors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  )}
                  <MutationError
                    error={saveDraft.error ?? previewDraft.error ?? applyDraft.error}
                  />
                  {previewResult !== null && (
                    <CompositionPreview
                      preview={previewResult}
                      editor={editor}
                      dispatch={dispatch}
                      reason={reason}
                      setReason={setReason}
                      confirmLargeScope={confirmLargeScope}
                      setConfirmLargeScope={setConfirmLargeScope}
                      warningsAcknowledged={warningsAcknowledged}
                      canApply={canConfigure}
                      busy={busy}
                      onApply={() => void applyPreview()}
                    />
                  )}
                  {applyResult !== null && (
                    <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm">
                      <div className="font-medium">
                        {applyResult.changed
                          ? t('provision.composition.applied', 'Composition applied')
                          : t(
                              'provision.composition.alreadyCurrent',
                              'Composition already current',
                            )}
                      </div>
                      <p className="text-muted-foreground">
                        {t(
                          'provision.composition.revisions',
                          'Composition revision {composition}; desired-state revision {desired}.',
                        )
                          .replace('{composition}', String(applyResult.compositionRevision))
                          .replace('{desired}', String(applyResult.desiredStateRevision))}
                      </p>
                      {onOpenRollouts !== undefined && accessMode === 'admin' && (
                        <Button
                          className="mt-3"
                          size="sm"
                          variant="outline"
                          onClick={onOpenRollouts}
                        >
                          {t(
                            'provision.composition.continueToRollouts',
                            'Continue to deployment rollouts',
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </section>
              </>
            )}
          {authoringPath === 'manual' &&
            canAdmin &&
            profile.data !== undefined &&
            draft.isSuccess &&
            desired.data !== undefined && (
              <AdvancedCompositionEditor
                application={selectedApplication}
                profile={profile.data}
                draft={draft.data}
                desiredValues={desired.data.values}
              />
            )}
          {accessMode === 'admin' && <CompositionHistory applicationId={selectedApplication.id} />}
          <CompositionDriftPanel
            item={drift.find((item) => item.applicationId === selectedApplication.id)}
          />
        </>
      )}
    </div>
  );
}

function TechnicianDeliveryStatus({
  inventory,
  applicationId,
}: {
  inventory: ProvisionEndpointInventory | undefined;
  applicationId: string;
}) {
  const t = useT('admin_ui');
  const components =
    inventory?.applications.find((item) => item.applicationInstanceId === applicationId)
      ?.components ?? [];
  return (
    <section className="rounded-lg border p-4">
      <h2 className="font-semibold">
        {t('provision.composition.installedComponents', 'Installed component status')}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {t(
          'provision.composition.installedComponentsHelp',
          'Runtime evidence reported by this terminal, including independent package rollback state.',
        )}
      </p>
      {components.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          {t(
            'provision.composition.noComponentInventory',
            'No current component inventory. Provision will remain pending until the terminal reports.',
          )}
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {components.map((component) => {
            const evidence = component.packageEvidence;
            return (
              <div
                key={component.observedComponentId}
                className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-sm"
              >
                <div>
                  <div className="font-medium">{component.componentId}</div>
                  <div className="text-xs text-muted-foreground">
                    {t('provision.composition.componentVersion', 'version {version}').replace(
                      '{version}',
                      component.semanticVersion,
                    )}
                    {evidence?.lastKnownGoodVersionId === null ||
                    evidence?.lastKnownGoodVersionId === undefined
                      ? ''
                      : ` · LKG ${evidence.lastKnownGoodVersionId}`}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={component.health === 'healthy' ? 'success' : 'outline'}>
                    {component.health}
                  </Badge>
                  <Badge
                    variant={
                      evidence?.lifecycleState === 'failed' || evidence?.verification === 'rejected'
                        ? 'destructive'
                        : 'outline'
                    }
                  >
                    {technicianLifecycleLabel(evidence?.lifecycleState ?? null, t)}
                  </Badge>
                  {evidence !== null && <Badge variant="outline">{evidence.verification}</Badge>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * `translate` defaults to the baked English so the pure-data spec can call this
 * with a lifecycle state alone; the component passes its `useT` binding.
 */
export function technicianLifecycleLabel(
  state:
    | 'staged'
    | 'installed'
    | 'starting'
    | 'active'
    | 'failed'
    | 'rolling_back'
    | 'rolled_back'
    | null,
  translate: (key: string, bakedEnglish: string) => string = (_key, bakedEnglish) => bakedEnglish,
): string {
  switch (state) {
    case 'staged':
      return translate('provision.composition.lifecycleStaged', 'pending install');
    case 'installed':
      return translate('provision.composition.lifecycleInstalled', 'installed / restart pending');
    case 'starting':
      return translate('provision.composition.lifecycleStarting', 'starting');
    case 'active':
      return translate('provision.composition.lifecycleActive', 'active');
    case 'failed':
      return translate('provision.composition.lifecycleFailed', 'failed');
    case 'rolling_back':
      return translate('provision.composition.lifecycleRollingBack', 'rolling back');
    case 'rolled_back':
      return translate('provision.composition.lifecycleRolledBack', 'rolled back to LKG');
    case null:
      return translate('provision.composition.lifecycleNone', 'bundled / no package');
  }
}

function ManagedRoleCard({
  applicationId,
  role,
  showIncompatible,
  equipmentCatalogue,
  softwareCatalogue,
  selectedSoftwareVersionIds,
  inventoryPorts,
  accessMode,
  dispatch,
}: {
  applicationId: string;
  role: ManagedHardwareRoleState;
  showIncompatible: boolean;
  equipmentCatalogue: ProvisionEquipmentCatalogueModel[];
  softwareCatalogue: ProvisionSoftwareCatalogueComponent[];
  selectedSoftwareVersionIds: string[];
  inventoryPorts: Array<{
    portId: string;
    label: string;
    status: string;
    health: string;
    identityStrength: string;
    shareMode: string;
    lastSeenAt: string | null;
  }>;
  accessMode: ProvisionAccessMode;
  dispatch: Dispatch<ManagedCompositionEditorAction>;
}) {
  const t = useT('admin_ui');
  const selection = compatibilitySelection(role, selectedSoftwareVersionIds);
  const request = (
    kind: CandidateKind,
    enabled: boolean,
    currentCandidateId: string | undefined,
  ): ProvisionCompositionCandidateRequest | null =>
    enabled
      ? {
          schemaVersion: 1,
          kind,
          selection,
          includeIncompatible: showIncompatible,
          currentCandidateId: currentCandidateId ?? null,
        }
      : null;
  const equipmentCandidates = useProvisionCompositionCandidates(
    applicationId,
    request('equipment_model', role.enabled, role.currentCandidateIds.equipment_model),
    accessMode,
  );
  const assetCandidates = useProvisionCompositionCandidates(
    applicationId,
    request(
      'asset',
      role.enabled && role.equipmentModelRevisionId !== null,
      role.currentCandidateIds.asset,
    ),
    accessMode,
  );
  const connectionCandidates = useProvisionCompositionCandidates(
    applicationId,
    request(
      'connection_method',
      role.enabled && role.equipmentModelRevisionId !== null,
      role.currentCandidateIds.connection_method,
    ),
    accessMode,
  );
  const portCandidates = useProvisionCompositionCandidates(
    applicationId,
    request(
      'endpoint_port',
      role.enabled && role.connectionProfileId !== null,
      role.currentCandidateIds.endpoint_port,
    ),
    accessMode,
  );
  const componentCandidates = useProvisionCompositionCandidates(
    applicationId,
    request(
      'component',
      role.enabled && role.connectionProfileId !== null,
      role.currentCandidateIds.component,
    ),
    accessMode,
  );
  const versionCandidates = useProvisionCompositionCandidates(
    applicationId,
    request(
      'component_version',
      role.enabled && role.adapterComponentId !== null,
      role.currentCandidateIds.component_version,
    ),
    accessMode,
  );
  const selectedEquipment = equipmentRevision(equipmentCatalogue, role.equipmentModelRevisionId);
  const selectedConnection = connectionProfile(equipmentCatalogue, role.connectionProfileId);
  const selectedAdapter = componentVersion(softwareCatalogue, role.adapterComponentVersionId);
  const filteredVersions = filterComponentVersions(versionCandidates.data, role.adapterComponentId);
  const selectedPort = inventoryPorts.find((port) => port.portId === role.endpointPortId);

  useAutomaticCandidate(equipmentCandidates.data, role.equipmentModelRevisionId, (id) => {
    const revision = equipmentRevision(equipmentCatalogue, id);
    dispatch({
      type: 'select-equipment',
      roleKey: role.roleKey,
      id,
      configurationDefaults: revision?.configurationDefaults,
    });
  });
  useAutomaticCandidate(assetCandidates.data, role.assetId, (id) =>
    dispatch({ type: 'select-asset', roleKey: role.roleKey, id }),
  );
  useAutomaticCandidate(connectionCandidates.data, role.connectionProfileId, (id) => {
    const connection = connectionProfile(equipmentCatalogue, id);
    dispatch({
      type: 'select-connection',
      roleKey: role.roleKey,
      id,
      defaults: connection?.defaults,
    });
  });
  useAutomaticCandidate(portCandidates.data, role.endpointPortId, (id) =>
    dispatch({ type: 'select-port', roleKey: role.roleKey, id }),
  );
  useAutomaticCandidate(componentCandidates.data, role.adapterComponentId, (id) =>
    dispatch({ type: 'select-adapter-component', roleKey: role.roleKey, id }),
  );
  useAutomaticCandidate(filteredVersions, role.adapterComponentVersionId, (id) =>
    dispatch({
      type: 'select-adapter-version',
      roleKey: role.roleKey,
      id,
      defaults: componentVersion(softwareCatalogue, id)?.version.configurationDefaults,
    }),
  );

  return (
    <article className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-medium">{role.displayName}</h3>
            <Badge variant={role.requiredNow ? 'destructive' : 'outline'}>
              {role.requiredNow
                ? t('provision.composition.requiredNow', 'required now')
                : role.requirement}
            </Badge>
            <Badge variant="outline">
              {t('provision.composition.roleScope', '{scope} scope').replace('{scope}', role.scope)}
            </Badge>
            <Badge variant="outline">{role.sharing.replaceAll('_', ' ')}</Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {role.requirementReason ??
              (role.requiredNow
                ? t(
                    'provision.composition.roleRequiredHelp',
                    'The active composition profile requires this equipment role.',
                  )
                : t(
                    'provision.composition.roleOptionalHelp',
                    'This role is optional for the current application facts.',
                  ))}
          </p>
          {role.conditionEvidence.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              {role.conditionEvidence.map((evidence) => (
                <li key={`${evidence.code}-${evidence.message}`}>
                  {evidence.matched ? '✓' : '○'} {evidence.message}
                </li>
              ))}
            </ul>
          )}
        </div>
        {!role.requiredNow && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={role.enabled}
              onChange={(event) =>
                dispatch({
                  type: 'toggle-role',
                  roleKey: role.roleKey,
                  enabled: event.target.checked,
                })
              }
            />
            {t('provision.composition.includeRole', 'Include role')}
          </label>
        )}
      </div>
      {role.enabled && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <CandidateSelect
            label={t('provision.composition.equipmentModel', 'Equipment make / model')}
            kind="equipment_model"
            selectedId={role.equipmentModelRevisionId}
            currentCandidateId={role.currentCandidateIds.equipment_model}
            response={equipmentCandidates.data}
            loading={equipmentCandidates.isFetching}
            onChange={(id) => {
              const revision = equipmentRevision(equipmentCatalogue, id);
              dispatch({
                type: 'select-equipment',
                roleKey: role.roleKey,
                id,
                configurationDefaults: revision?.configurationDefaults,
              });
            }}
          />
          <CandidateSelect
            label={t('provision.composition.physicalAsset', 'Physical asset')}
            kind="asset"
            disabled={role.equipmentModelRevisionId === null}
            selectedId={role.assetId}
            currentCandidateId={role.currentCandidateIds.asset}
            response={assetCandidates.data}
            loading={assetCandidates.isFetching}
            onChange={(id) => dispatch({ type: 'select-asset', roleKey: role.roleKey, id })}
          />
          <CandidateSelect
            label={t('provision.composition.connectionMethod', 'Connection method')}
            kind="connection_method"
            disabled={role.equipmentModelRevisionId === null}
            selectedId={role.connectionProfileId}
            currentCandidateId={role.currentCandidateIds.connection_method}
            response={connectionCandidates.data}
            loading={connectionCandidates.isFetching}
            onChange={(id) => {
              const connection = connectionProfile(equipmentCatalogue, id);
              dispatch({
                type: 'select-connection',
                roleKey: role.roleKey,
                id,
                defaults: connection?.defaults,
              });
            }}
          />
          <CandidateSelect
            label={t('provision.composition.endpointPort', 'Available endpoint port')}
            kind="endpoint_port"
            allowNone
            disabled={role.connectionProfileId === null}
            selectedId={role.endpointPortId}
            currentCandidateId={role.currentCandidateIds.endpoint_port}
            response={portCandidates.data}
            loading={portCandidates.isFetching}
            onChange={(id) => dispatch({ type: 'select-port', roleKey: role.roleKey, id })}
          />
          {accessMode === 'admin' && (
            <>
              <CandidateSelect
                label={t('provision.composition.adapterIntegration', 'Adapter / integration')}
                kind="component"
                disabled={role.connectionProfileId === null}
                selectedId={role.adapterComponentId}
                currentCandidateId={role.currentCandidateIds.component}
                response={componentCandidates.data}
                loading={componentCandidates.isFetching}
                onChange={(id) =>
                  dispatch({ type: 'select-adapter-component', roleKey: role.roleKey, id })
                }
              />
              <CandidateSelect
                label={t('provision.composition.adapterVersion', 'Adapter version')}
                kind="component_version"
                disabled={role.adapterComponentId === null}
                selectedId={role.adapterComponentVersionId}
                currentCandidateId={role.currentCandidateIds.component_version}
                response={filteredVersions}
                loading={versionCandidates.isFetching}
                onChange={(id) =>
                  dispatch({
                    type: 'select-adapter-version',
                    roleKey: role.roleKey,
                    id,
                    defaults: componentVersion(softwareCatalogue, id)?.version
                      .configurationDefaults,
                  })
                }
              />
            </>
          )}
        </div>
      )}
      {role.enabled && selectedPort !== undefined && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <Badge variant={selectedPort.status === 'present' ? 'success' : 'outline'}>
            {selectedPort.status}
          </Badge>
          <Badge variant="outline">{selectedPort.health}</Badge>
          <Badge variant="outline">
            {t('provision.composition.portIdentity', '{strength} identity').replace(
              '{strength}',
              selectedPort.identityStrength,
            )}
          </Badge>
          <Badge variant="outline">{selectedPort.shareMode}</Badge>
          <span className="text-muted-foreground">
            {t('provision.composition.portLastSeen', 'Last seen {when}').replace(
              '{when}',
              selectedPort.lastSeenAt ?? t('provision.composition.never', 'never'),
            )}
          </span>
        </div>
      )}
      {role.enabled && selectedEquipment !== null && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <SchemaFields
            title={t('provision.composition.equipmentConfiguration', 'Equipment configuration')}
            schema={selectedEquipment.configurationSchema}
            values={role.config}
            onChange={(key, value) =>
              dispatch({ type: 'set-role-config', roleKey: role.roleKey, key, value })
            }
          />
          <div>
            <h4 className="mb-2 text-sm font-medium">
              {t('provision.composition.connectionConfiguration', 'Connection configuration')}
            </h4>
            <div className="space-y-2">
              {selectedConnection?.fields.map((field) =>
                field.valueType === 'secret_reference' ? (
                  <TypedField
                    key={field.key}
                    label={field.label}
                    type="string"
                    required={field.required}
                    value={role.secretReference}
                    onChange={(value) =>
                      dispatch({
                        type: 'set-secret-reference',
                        roleKey: role.roleKey,
                        value: String(value),
                      })
                    }
                  />
                ) : (
                  <TypedField
                    key={field.key}
                    label={field.label}
                    type={field.valueType}
                    required={field.required}
                    value={role.connectionParameters[field.key] ?? field.defaultValue ?? ''}
                    allowedValues={field.allowedValues}
                    minimum={field.minimum}
                    maximum={field.maximum}
                    onChange={(value) =>
                      dispatch({
                        type: 'set-connection-parameter',
                        roleKey: role.roleKey,
                        key: field.key,
                        value,
                      })
                    }
                  />
                ),
              )}
              {!selectedConnection?.fields.some(
                (field) => field.valueType === 'secret_reference',
              ) && (
                <TypedField
                  label={t(
                    'provision.composition.secretReferenceField',
                    'Secret reference (never a secret value)',
                  )}
                  type="string"
                  required={false}
                  value={role.secretReference}
                  onChange={(value) =>
                    dispatch({
                      type: 'set-secret-reference',
                      roleKey: role.roleKey,
                      value: String(value),
                    })
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}
      {role.enabled && selectedAdapter !== null && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            {accessMode === 'technician'
              ? t('provision.composition.autoResolved', 'Automatically resolved:')
              : t('provision.composition.adapter', 'Adapter')}{' '}
            {selectedAdapter.component.displayName} {selectedAdapter.version.semanticVersion} ·{' '}
            {certificationLabel(selectedAdapter.version.certification, t)} ·{' '}
            {t('provision.composition.adapterActivation', 'activation {policy}').replace(
              '{policy}',
              selectedAdapter.version.activationPolicy.replaceAll('_', ' '),
            )}
          </p>
          <SchemaFields
            title={t('provision.composition.adapterConfiguration', 'Adapter configuration')}
            schema={selectedAdapter.version.configurationSchema}
            values={role.adapterConfiguration}
            onChange={(key, value) =>
              dispatch({ type: 'set-adapter-config', roleKey: role.roleKey, key, value })
            }
          />
        </div>
      )}
    </article>
  );
}

function SoftwareSection({
  applicationId,
  editor,
  showIncompatible,
  catalogue,
  requiredCapabilities,
  optionalCapabilities,
  accessMode,
  dispatch,
}: {
  applicationId: string;
  editor: ManagedCompositionEditorState;
  showIncompatible: boolean;
  catalogue: ProvisionSoftwareCatalogueComponent[];
  requiredCapabilities: string[];
  optionalCapabilities: string[];
  accessMode: ProvisionAccessMode;
  dispatch: Dispatch<ManagedCompositionEditorAction>;
}) {
  const t = useT('admin_ui');
  const [newRoleKey, setNewRoleKey] = useState('');
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">
            {t('provision.composition.softwareComponents', 'Software components')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              'provision.composition.softwareComponentsHelp',
              'Select compatible published releases. Final validation resolves the complete dependency graph and stable installation order.',
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            aria-label={t('provision.composition.newRoleKey', 'New software role key')}
            placeholder={t('provision.composition.roleKeyPlaceholder', 'Role key, e.g. pos_core')}
            value={newRoleKey}
            onChange={(event) => setNewRoleKey(normalizeRoleKey(event.target.value))}
          />
          <Button
            size="sm"
            variant="outline"
            disabled={newRoleKey.length === 0 || editor.software[newRoleKey] !== undefined}
            onClick={() => {
              dispatch({ type: 'add-software', roleKey: newRoleKey });
              setNewRoleKey('');
            }}
          >
            {t('provision.composition.addComponent', 'Add component')}
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="text-muted-foreground">
          {t('provision.composition.requiredCapabilities', 'Required capabilities:')}
        </span>
        {requiredCapabilities.length === 0 ? (
          <Badge variant="outline">
            {t('provision.composition.noneDeclared', 'none declared')}
          </Badge>
        ) : (
          requiredCapabilities.map((capability) => (
            <Badge key={capability} variant="outline">
              {capability}
            </Badge>
          ))
        )}
        {optionalCapabilities.length > 0 && (
          <span className="ml-2 text-muted-foreground">
            {t('provision.composition.optionalCapabilities', 'Optional:')}
          </span>
        )}
        {optionalCapabilities.map((capability) => (
          <Badge key={capability} variant="outline">
            {capability}
          </Badge>
        ))}
        {catalogue.length === 0 && (
          <Badge variant="outline">
            {t('provision.composition.catalogueLoading', 'Catalogue loading…')}
          </Badge>
        )}
      </div>
      {Object.values(editor.software).length === 0 && (
        <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          {t(
            'provision.composition.noSoftwareSlots',
            'No explicit software component slots yet. Add core, UI, driver, sidecar, fiscal, payment, or service roles as required by this composition.',
          )}
        </div>
      )}
      {Object.values(editor.software).map((component) => (
        <SoftwareComponentCard
          key={component.roleKey}
          applicationId={applicationId}
          component={component}
          selectedVersionIds={Object.values(editor.software).flatMap((item) =>
            item.componentVersionId === null ? [] : [item.componentVersionId],
          )}
          showIncompatible={showIncompatible}
          catalogue={catalogue}
          accessMode={accessMode}
          dispatch={dispatch}
        />
      ))}
    </section>
  );
}

function SoftwareComponentCard({
  applicationId,
  component,
  selectedVersionIds,
  showIncompatible,
  catalogue,
  accessMode,
  dispatch,
}: {
  applicationId: string;
  component: ManagedSoftwareState;
  selectedVersionIds: string[];
  showIncompatible: boolean;
  catalogue: ProvisionSoftwareCatalogueComponent[];
  accessMode: ProvisionAccessMode;
  dispatch: Dispatch<ManagedCompositionEditorAction>;
}) {
  const t = useT('admin_ui');
  const selection = {
    roleKey: null,
    equipmentModelRevisionId: null,
    assetId: null,
    endpointPortId: null,
    peripheralBindingId: null,
    connectionProfileId: null,
    adapterComponentVersionId: null,
    componentVersionIds: selectedVersionIds,
    manualMode: false,
  };
  const componentCandidates = useProvisionCompositionCandidates(
    applicationId,
    {
      schemaVersion: 1,
      kind: 'component',
      selection,
      includeIncompatible: showIncompatible,
      currentCandidateId: component.currentComponentId,
    },
    accessMode,
  );
  const versionCandidates = useProvisionCompositionCandidates(
    applicationId,
    component.componentId === null
      ? null
      : {
          schemaVersion: 1,
          kind: 'component_version',
          selection,
          includeIncompatible: showIncompatible,
          currentCandidateId: component.currentComponentVersionId,
        },
    accessMode,
  );
  const filteredVersions = filterComponentVersions(versionCandidates.data, component.componentId);
  const selectedVersion = componentVersion(catalogue, component.componentVersionId);
  useAutomaticCandidate(componentCandidates.data, component.componentId, (id) =>
    dispatch({ type: 'select-software-component', roleKey: component.roleKey, id }),
  );
  useAutomaticCandidate(filteredVersions, component.componentVersionId, (id) => {
    const selected = componentVersion(catalogue, id);
    dispatch({
      type: 'select-software-version',
      roleKey: component.roleKey,
      id,
      defaults: selected?.version.configurationDefaults,
    });
  });
  return (
    <article className="rounded-lg border p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-medium">{component.roleKey.replaceAll('_', ' ')}</h3>
          <code className="text-xs text-muted-foreground">{component.roleKey}</code>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => dispatch({ type: 'remove-software', roleKey: component.roleKey })}
        >
          {t('provision.composition.removeComponent', 'Remove')}
        </Button>
      </div>
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <CandidateSelect
          label={t('provision.composition.component', 'Component')}
          kind="component"
          selectedId={component.componentId}
          currentCandidateId={component.currentComponentId ?? undefined}
          response={componentCandidates.data}
          loading={componentCandidates.isFetching}
          onChange={(id) =>
            dispatch({ type: 'select-software-component', roleKey: component.roleKey, id })
          }
        />
        <CandidateSelect
          label={t('provision.composition.publishedVersion', 'Published version')}
          kind="component_version"
          disabled={component.componentId === null}
          selectedId={component.componentVersionId}
          currentCandidateId={component.currentComponentVersionId ?? undefined}
          response={filteredVersions}
          loading={versionCandidates.isFetching}
          onChange={(id) => {
            const selected = componentVersion(catalogue, id);
            dispatch({
              type: 'select-software-version',
              roleKey: component.roleKey,
              id,
              defaults: selected?.version.configurationDefaults,
            });
          }}
        />
      </div>
      {selectedVersion !== null && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline">{selectedVersion.version.componentType}</Badge>
            <Badge variant="outline">
              {selectedVersion.version.deliveryMode.replaceAll('_', ' ')}
            </Badge>
            <Badge variant="outline">
              {selectedVersion.version.activationPolicy.replaceAll('_', ' ')}
            </Badge>
            <Badge variant={selectedVersion.version.trusted ? 'success' : 'outline'}>
              {selectedVersion.version.trusted
                ? t('provision.composition.trusted', 'trusted')
                : t('provision.composition.untrusted', 'untrusted')}
            </Badge>
          </div>
          <SchemaFields
            title={t('provision.composition.componentConfiguration', 'Component configuration')}
            schema={selectedVersion.version.configurationSchema}
            values={component.configuration}
            onChange={(key, value) =>
              dispatch({ type: 'set-software-config', roleKey: component.roleKey, key, value })
            }
          />
        </div>
      )}
    </article>
  );
}

function CandidateSelect({
  label,
  kind,
  selectedId,
  currentCandidateId,
  response,
  loading,
  disabled = false,
  allowNone = false,
  onChange,
}: {
  label: string;
  kind: CandidateKind;
  selectedId: string | null;
  currentCandidateId?: string;
  response?: ProvisionCompositionCandidateResponse;
  loading: boolean;
  disabled?: boolean;
  allowNone?: boolean;
  onChange: (id: string | null) => void;
}) {
  const t = useT('admin_ui');
  const value = selectedId ?? currentCandidateId ?? '';
  const selected = response?.candidates.find((candidate) => candidate.candidateId === value);
  return (
    <Field label={label}>
      <Select
        aria-label={label}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      >
        <option value="">
          {loading
            ? t('provision.composition.loadingChoices', 'Loading compatible choices…')
            : allowNone
              ? t('provision.composition.noPortOption', 'No inventoried port / external method')
              : t('provision.composition.selectPlaceholder', 'Select {label}').replace(
                  '{label}',
                  label.toLowerCase(),
                )}
        </option>
        {response?.candidates.map((candidate) => (
          <option key={candidate.candidateId} value={candidate.candidateId}>
            {candidate.displayName}
            {candidate.eligible
              ? ''
              : ` — ${t('provision.composition.incompatible', 'incompatible')}`}
            {candidate.metadata['customLegacy'] === true
              ? ` — ${t('provision.composition.customLegacy', 'Custom/legacy')}`
              : ''}
          </option>
        ))}
      </Select>
      {selected !== undefined && (
        <div className="mt-1 space-y-1 text-xs">
          <div className="flex flex-wrap gap-1">
            <Badge variant={selected.eligible ? 'success' : 'outline'}>
              {selected.eligible
                ? t('provision.composition.compatible', 'compatible')
                : t('provision.composition.incompatible', 'incompatible')}
            </Badge>
            <Badge variant="outline">{selected.lifecycle.replaceAll('_', ' ')}</Badge>
            {selected.metadata['customLegacy'] === true && (
              <Badge variant="outline">
                {t('provision.composition.customLegacyRetained', 'Custom/legacy retained')}
              </Badge>
            )}
          </div>
          {selected.reasons.map((reason) => (
            <div
              key={`${reason.code}-${reason.message}`}
              className={selected.eligible ? 'text-muted-foreground' : 'text-destructive'}
            >
              <code>{reason.code}</code> · {reason.message}
            </div>
          ))}
        </div>
      )}
      {kind === 'endpoint_port' && response?.candidates.length === 0 && !loading && (
        <p className="mt-1 text-xs text-muted-foreground">
          {t(
            'provision.composition.noCompatiblePort',
            'No compatible observed port is available; external/manual methods may remain portless.',
          )}
        </p>
      )}
    </Field>
  );
}

function CompositionPreview({
  preview,
  editor,
  dispatch,
  reason,
  setReason,
  confirmLargeScope,
  setConfirmLargeScope,
  warningsAcknowledged,
  canApply,
  busy,
  onApply,
}: {
  preview: ProvisionCompositionDraftPreview;
  editor: ManagedCompositionEditorState;
  dispatch: Dispatch<ManagedCompositionEditorAction>;
  reason: string;
  setReason: (value: string) => void;
  confirmLargeScope: boolean;
  setConfirmLargeScope: (value: boolean) => void;
  warningsAcknowledged: boolean;
  canApply: boolean;
  busy: boolean;
  onApply: () => void;
}) {
  const t = useT('admin_ui');
  return (
    <div className="mt-4 space-y-4 border-t pt-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Summary
          label={t('provision.composition.summaryValidation', 'Validation')}
          value={
            preview.validation.valid
              ? t('provision.composition.summaryValid', 'Valid')
              : t('provision.composition.summaryBlocked', 'Blocked')
          }
        />
        <Summary
          label={t('provision.composition.summaryDesiredChanges', 'Desired changes')}
          value={String(preview.desiredStateChanges.length)}
        />
        <Summary
          label={t('provision.composition.summaryPortActions', 'Port actions')}
          value={String(preview.portClaims.length)}
        />
        <Summary
          label={t('provision.composition.summaryActivation', 'Activation')}
          value={preview.validation.strictestActivationPolicy.replaceAll('_', ' ')}
        />
      </div>
      {preview.validation.findings.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium">
            {t('provision.composition.validationFindings', 'Validation findings')}
          </h3>
          {preview.validation.findings.map((finding) => (
            <div
              key={`${finding.reason.code}-${finding.entityId}`}
              className="rounded-md border p-2 text-sm"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={finding.severity === 'error' ? 'destructive' : 'outline'}>
                  {finding.severity}
                </Badge>
                <code>{finding.reason.code}</code>
                <span>{finding.reason.message}</span>
              </div>
              {finding.acknowledgeable && (
                <label className="mt-2 flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={editor.acknowledgedWarningCodes.includes(finding.reason.code)}
                    onChange={(event) =>
                      dispatch({
                        type: 'acknowledge-warning',
                        code: finding.reason.code,
                        acknowledged: event.target.checked,
                      })
                    }
                  />
                  {t(
                    'provision.composition.acknowledgeManualWarning',
                    'Acknowledge this allowed manual-evidence warning, then save and preview again',
                  )}
                </label>
              )}
            </div>
          ))}
        </div>
      )}
      {preview.portClaims.length > 0 && (
        <div>
          <h3 className="text-sm font-medium">
            {t('provision.composition.portClaimPlan', 'Port claim plan')}
          </h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {preview.portClaims.map((claim) => (
              <Badge
                key={`${claim.roleKey}-${claim.endpointPortId}-${claim.action}`}
                variant="outline"
              >
                {claim.roleKey}: {claim.action} {claim.endpointPortId}
              </Badge>
            ))}
          </div>
        </div>
      )}
      {preview.validation.dependencyOrder.length > 0 && (
        <div>
          <h3 className="text-sm font-medium">
            {t('provision.composition.dependencyOrder', 'Dependency installation order')}
          </h3>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            {preview.validation.dependencyOrder.map((componentId) => (
              <li key={componentId}>{componentId}</li>
            ))}
          </ol>
        </div>
      )}
      <Field label={t('provision.composition.applyReason', 'Reason for applying composition')}>
        <Input
          required
          value={reason}
          placeholder={t(
            'provision.composition.applyReasonPlaceholder',
            'Describe the reviewed composition change',
          )}
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>
      {preview.largeScopeConfirmationRequired && (
        <label className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
          <input
            type="checkbox"
            checked={confirmLargeScope}
            onChange={(event) => setConfirmLargeScope(event.target.checked)}
          />
          {t(
            'provision.composition.confirmLargeScope',
            'Confirm impact on {count} same-host applications',
          ).replace('{count}', String(preview.affectedApplicationIds.length))}
        </label>
      )}
      <Button
        disabled={
          !canApply ||
          busy ||
          !preview.validation.valid ||
          !warningsAcknowledged ||
          reason.trim().length === 0 ||
          (preview.largeScopeConfirmationRequired && !confirmLargeScope)
        }
        onClick={onApply}
      >
        {t('provision.composition.confirmApply', 'Confirm and apply composition')}
      </Button>
    </div>
  );
}

export function configurationFields(schema: Record<string, unknown>) {
  const properties = recordValue(schema['properties']);
  const required = new Set(
    Array.isArray(schema['required'])
      ? schema['required'].filter((value): value is string => typeof value === 'string')
      : [],
  );
  return Object.entries(properties)
    .map(([key, value]) => {
      const definition = recordValue(value);
      const type = definition['type'];
      const enumValues = Array.isArray(definition['enum']) ? definition['enum'] : undefined;
      return {
        key,
        label:
          typeof definition['title'] === 'string' ? definition['title'] : key.replaceAll('_', ' '),
        type:
          enumValues !== undefined
            ? ('enum' as const)
            : type === 'integer' || type === 'number'
              ? ('integer' as const)
              : type === 'boolean'
                ? ('boolean' as const)
                : ('string' as const),
        required: required.has(key),
        allowedValues: enumValues,
        minimum: typeof definition['minimum'] === 'number' ? definition['minimum'] : undefined,
        maximum: typeof definition['maximum'] === 'number' ? definition['maximum'] : undefined,
      };
    })
    .sort((left, right) => left.key.localeCompare(right.key));
}

function SchemaFields({
  title,
  schema,
  values,
  onChange,
}: {
  title: string;
  schema: Record<string, unknown>;
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const t = useT('admin_ui');
  const fields = configurationFields(schema);
  if (fields.length === 0) {
    return (
      <div>
        <h4 className="text-sm font-medium">{title}</h4>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('provision.composition.noAdditionalFields', 'No additional fields are declared.')}
        </p>
      </div>
    );
  }
  return (
    <div>
      <h4 className="mb-2 text-sm font-medium">{title}</h4>
      <div className="space-y-2">
        {fields.map(({ key, ...field }) => (
          <TypedField
            key={key}
            {...field}
            value={values[key] ?? ''}
            onChange={(value) => onChange(key, value)}
          />
        ))}
      </div>
    </div>
  );
}

function TypedField({
  label,
  type,
  required,
  value,
  allowedValues,
  minimum,
  maximum,
  onChange,
}: {
  label: string;
  type: 'string' | 'integer' | 'boolean' | 'enum';
  required: boolean;
  value: unknown;
  allowedValues?: unknown[];
  minimum?: number;
  maximum?: number;
  onChange: (value: unknown) => void;
}) {
  const t = useT('admin_ui');
  if (type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
        {label}
        {required ? ' *' : ''}
      </label>
    );
  }
  if (type === 'enum') {
    return (
      <Field label={`${label}${required ? ' *' : ''}`}>
        <Select value={String(value)} onChange={(event) => onChange(event.target.value)}>
          <option value="">{t('provision.composition.selectValue', 'Select value')}</option>
          {allowedValues?.map((allowed) => (
            <option key={JSON.stringify(allowed)} value={String(allowed)}>
              {String(allowed)}
            </option>
          ))}
        </Select>
      </Field>
    );
  }
  return (
    <Field label={`${label}${required ? ' *' : ''}`}>
      <Input
        required={required}
        type={type === 'integer' ? 'number' : 'text'}
        min={minimum}
        max={maximum}
        value={typeof value === 'string' || typeof value === 'number' ? value : ''}
        onChange={(event) =>
          onChange(type === 'integer' ? Number(event.target.value) : event.target.value)
        }
      />
    </Field>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 font-medium">{value}</div>
    </div>
  );
}

function MutationError({ error }: { error: unknown }) {
  const t = useT('admin_ui');
  if (error === null || error === undefined) return null;
  return (
    <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
      {errorMessage(error, t('provision.composition.requestFailed', 'Provision request failed'))}
    </div>
  );
}

function filterComponentVersions(
  response: ProvisionCompositionCandidateResponse | undefined,
  componentId: string | null,
): ProvisionCompositionCandidateResponse | undefined {
  if (response === undefined || componentId === null) return response;
  const candidates = response.candidates.filter(
    (candidate) =>
      candidate.metadata['componentId'] === componentId ||
      candidate.metadata['customLegacy'] === true,
  );
  const auto = candidates.filter((candidate) => candidate.eligible);
  return {
    ...response,
    candidates,
    autoSelectCandidateId:
      response.autoSelectCandidateId !== null &&
      candidates.some((candidate) => candidate.candidateId === response.autoSelectCandidateId)
        ? response.autoSelectCandidateId
        : auto.length === 1
          ? (auto[0]?.candidateId ?? null)
          : null,
  };
}

function useAutomaticCandidate(
  response: ProvisionCompositionCandidateResponse | undefined,
  selectedId: string | null,
  onSelect: (id: string) => void,
) {
  const auto = response?.autoSelectCandidateId ?? null;
  useEffect(() => {
    if (selectedId === null && auto !== null) onSelect(auto);
  }, [auto, onSelect, selectedId]);
}

function normalizeRoleKey(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, '_')
    .slice(0, 80);
}

function compositionSourceKey(
  applicationId: string,
  draftVersion: number | null,
  profileVersionId: string | null,
  catalogue: ProvisionSoftwareCatalogueComponent[],
) {
  return [
    applicationId,
    draftVersion ?? 'new',
    profileVersionId ?? 'none',
    catalogue
      .flatMap((component) => component.versions.map((version) => version.componentVersionId))
      .sort()
      .join(','),
  ].join('|');
}

function errorMessage(error: unknown, fallback = 'Provision request failed') {
  if (typeof error !== 'object' || error === null) return String(error);
  if ('response' in error) {
    const response = (error as { response?: { data?: { message?: unknown } } }).response;
    const message = response?.data?.message;
    if (Array.isArray(message)) return message.join(', ');
    if (typeof message === 'string') return message;
  }
  if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  return fallback;
}

function recordValue(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
